import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { SendChatFeedbackRequest, SendChatMessageInput } from '@blaze-dashboard/ai-chat-domain';

import { ChatContextRegistry } from '../chat-context.registry';

import * as ChatConversationActions from './chat-conversation.actions';
import * as ChatFeedbackActions from './chat-feedback.actions';
import * as ChatInterruptActions from './chat-interrupt.actions';
import * as ChatMessagesActions from './chat-messages.actions';
import * as ChatPanelActions from './chat-panel.actions';
import * as ChatPollingActions from './chat-polling.actions';
import * as ChatTemplateConfigurationSetupActions from './chat-template-configuration-setup.actions';
import { getNewClientId } from './chat.models';
import * as ChatSelectors from './chat.selectors';

@Injectable({
  providedIn: 'root',
})
export class ChatFacade {
  private readonly store = inject(Store);
  private readonly contextRegistry = inject(ChatContextRegistry);

  readonly ambientContext$ = this.contextRegistry.currentContext$;

  readonly currentConversation$ = this.store.select(ChatSelectors.selectCurrentConversation);
  readonly currentConversationStatus$ = this.store.select(
    ChatSelectors.selectCurrentConversationStatus,
  );
  readonly currentConversationError$ = this.store.select(
    ChatSelectors.selectCurrentConversationError,
  );
  readonly establishedConversationProjectId$ = this.store.select(
    ChatSelectors.selectEstablishedConversationProjectId,
  );
  readonly panelProjectId$ = this.store.select(ChatSelectors.selectPanelProjectId);
  readonly coveredProjectId$ = this.store.select(ChatSelectors.selectCoveredProjectId);
  readonly conversationReadyToSend$ = this.store.select(
    ChatSelectors.selectConversationReadyToSend,
  );
  readonly hasLoadedMessages$ = this.store.select(ChatSelectors.selectHasLoadedMessages);
  readonly isTemplateConfigurationSetupRunning$ = this.store.select(
    ChatSelectors.selectIsTemplateConfigurationSetupRunning,
  );
  readonly pendingTemplateConfigurationSetupStep$ = this.store.select(
    ChatSelectors.selectPendingTemplateConfigurationSetupStep,
  );
  readonly pendingAnswer$ = this.store.select(ChatSelectors.selectPendingAnswer);
  readonly isConversationRunning$ = this.store.select(ChatSelectors.selectIsConversationRunning);
  readonly contextUsage$ = this.store.select(ChatSelectors.selectContextUsage);
  readonly messages$ = this.store.select(ChatSelectors.selectAllMessages);
  readonly pollingStatus$ = this.store.select(ChatSelectors.selectPollingStatus);
  readonly pollingError$ = this.store.select(ChatSelectors.selectPollingError);
  readonly livePollingConversationId$ = this.store.select(
    ChatSelectors.selectLivePollingConversationId,
  );
  readonly sendError$ = this.store.select(ChatSelectors.selectSendError);
  readonly lastFailedMessage$ = this.store.select(ChatSelectors.selectLastFailedMessage);
  readonly feedback$ = this.store.select(ChatSelectors.selectFeedback);
  readonly pendingActionTarget$ = this.store.select(ChatSelectors.selectPendingActionTarget);
  readonly lastPersistedMessageId$ = this.store.select(ChatSelectors.selectLastPersistedMessageId);
  readonly isUserWaitingForAnswer$ = this.store.select(ChatSelectors.selectIsUserWaitingForAnswer);
  readonly interruptError$ = this.store.select(ChatSelectors.selectInterruptError);
  readonly newConversationBoundaryMessageIds$ = this.store.select(
    ChatSelectors.selectNewConversationBoundaryMessageIds,
  );

  loadLatestConversation(projectId: string): void {
    this.store.dispatch(ChatConversationActions.loadLatestChatConversation({ projectId }));
  }

  startNewConversation(projectId: string): void {
    this.store.dispatch(ChatConversationActions.createChatConversation({ projectId }));
  }

  sendMessage(message: SendChatMessageInput): void {
    this.store.dispatch(
      ChatMessagesActions.sendChatMessage({ clientId: getNewClientId(), message }),
    );
  }

  openChat(projectId: string): void {
    this.store.dispatch(ChatPanelActions.openChatPanel({ projectId }));
  }

  retryPolling(): void {
    this.store.dispatch(ChatPollingActions.retryPollingChatMessages());
  }

  retrySend(): void {
    this.store.dispatch(ChatMessagesActions.retrySendChatMessage());
  }

  sendFeedback(feedback: SendChatFeedbackRequest, messageId: string): void {
    this.store.dispatch(ChatFeedbackActions.sendFeedback({ feedback, messageId }));
  }

  dismissAmbientContext(): void {
    this.contextRegistry.dismissCurrentContext();
  }

  interruptChat(): void {
    this.store.dispatch(ChatInterruptActions.interruptChat());
  }

  exitTemplateConfigurationSetup(): void {
    this.store.dispatch(ChatTemplateConfigurationSetupActions.exitTemplateConfigurationSetup());
  }
}
